<template>
  <div class="flex flex-col gap-4 pb-4">
    <p class="text-xs text-gray-500 leading-relaxed">
      המשאב משויך לסניף אחד וזמין להזמנה בשעותיו. שעות מותאמות אפשר לקבוע אחר כך מתוך המשאב.
    </p>

    <!-- Resource type picker. Only tan booth today; future equipment types slot in here. -->
    <div v-if="!isEdit" class="flex flex-col gap-1.5">
      <span class="text-sm font-medium">סוג המשאב</span>
      <button
        v-for="type in resourceTypes"
        :key="type.id"
        type="button"
        class="rounded-xl border-2 p-3 flex items-center gap-3 text-right transition-colors"
        :class="selectedType === type.id ? 'border-primary bg-primary-50/50' : 'border-gray-200 bg-white'"
        @click="selectedType = type.id"
      >
        <div class="w-11 h-11 rounded-xl flex items-center justify-center flex-none" :class="type.iconBg">
          <i :class="['pi', type.icon, type.iconColor]" class="text-lg" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="font-bold text-sm">{{ type.label }}</div>
          <div class="text-xs text-gray-500">{{ type.hint }}</div>
        </div>
      </button>
    </div>

    <div class="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium" for="resourceName">שם המשאב</label>
        <InputText id="resourceName" v-model="form.name" class="w-full" placeholder="תא שיזוף" />
      </div>

      <div v-if="branchOptions.length > 1" class="flex flex-col gap-1">
        <label class="text-sm font-medium" for="resourceBranch">סניף</label>
        <Select
          id="resourceBranch"
          v-model="form.branchId"
          :options="branchOptions"
          optionLabel="name"
          optionValue="id"
          class="w-full"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium" for="resourceCapacity">מספר עמדות</label>
        <InputNumber id="resourceCapacity" v-model="form.capacity" :min="1" :max="10" fluid />
        <span class="text-xs text-gray-500">כמה לקוחות יכולים להשתמש במשאב במקביל</span>
      </div>
    </div>

    <Button
      :label="isEdit ? 'שמירת שינויים' : 'הוספת המשאב'"
      class="w-full"
      :disabled="!canSave"
      :loading="isSaving"
      @click="save"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useToast } from 'vue-toastification';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import { IResource, MAIN_BRANCH_ID, ResourceTypeEnum, resourceTypeNames } from '@torim-app/types';
import { useAdminResourcesStore, useAdminStore } from '@app/stores';
import { trackEvent } from '@app/composables/posthog';

const props = defineProps<{
  resource?: IResource;
  onDone?: (resource: IResource) => void;
  onClose?: () => void;
}>();

const adminStore = useAdminStore();
const resourcesStore = useAdminResourcesStore();
const toast = useToast();

const isEdit = computed(() => !!props.resource?.id);
const branchOptions = computed(() => adminStore.getBranchSelectOptions);

// Generic registry of bookable equipment types. New kinds (massage chair, solarium
// room, ...) are added here.
const resourceTypes = [
  {
    id: ResourceTypeEnum.TAN_BOOTH,
    label: resourceTypeNames[ResourceTypeEnum.TAN_BOOTH],
    hint: 'ניתן להזמנה',
    icon: 'pi-sun',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
  },
];

const selectedType = ref<ResourceTypeEnum>(props.resource?.type ?? ResourceTypeEnum.TAN_BOOTH);

const form = reactive({
  name: props.resource?.name ?? '',
  branchId: props.resource?.branchId ?? MAIN_BRANCH_ID,
  capacity: props.resource?.capacity ?? 1,
});

const isSaving = ref(false);
const canSave = computed(() => !!form.name.trim() && !!selectedType.value);

const save = async () => {
  if (!canSave.value || isSaving.value) return;
  isSaving.value = true;
  try {
    const saved = await resourcesStore.saveResource({
      id: props.resource?.id,
      name: form.name.trim(),
      type: selectedType.value,
      branchId: form.branchId,
      capacity: form.capacity ?? 1,
    });
    trackEvent(isEdit.value ? 'resources_equipment_updated' : 'resources_equipment_created', {
      resource_type: selectedType.value,
    });
    toast.success(isEdit.value ? 'המשאב עודכן' : 'המשאב נוסף');
    props.onDone?.(saved);
  } catch (e) {
    console.error(e);
    toast.error('שמירת המשאב נכשלה');
  }
  isSaving.value = false;
};
</script>
